import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  buildPublicUrl,
  buildShareText,
  canNativeShare,
  copyLink,
  copyShareText,
  nativeShare
} from '../../utils/share';
import './ShareButton.css';

/**
 * Botón Compartir con menú propio RedCimarrona:
 *  - Copiar enlace
 *  - Copiar texto para compartir
 *  - Compartir... (solo si navigator.share existe)
 * No abre el share nativo directamente al click del botón.
 */
export default function ShareButton({
  tipo,
  publicId,
  title,
  text,
  data,
  className = '',
  label = 'Compartir'
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuId = useId();
  const url = buildPublicUrl(tipo, publicId);
  const shareTitle = title || '';
  const shareSummary = text || '';
  const shareBody = buildShareText({
    tipo,
    data,
    url,
    title: shareTitle,
    text: shareSummary
  });

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Eleva la ficha contenedora mientras el menú está abierto: sin esto,
  // la siguiente ficha del grid pinta encima (stacking context del hermano).
  useEffect(() => {
    if (!open || !wrapRef.current) return undefined;
    const card = wrapRef.current.closest(
      '.a-card, .urgent-card, .pub-card, .card'
    );
    if (!card) return undefined;
    card.classList.add('share-card-open');
    return () => card.classList.remove('share-card-open');
  }, [open]);

  if (!publicId || !url) return null;

  const onOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((v) => !v);
  };

  const onCopyLink = async (e) => {
    e.stopPropagation();
    const ok = await copyLink(url);
    if (ok) close();
  };

  const onCopyText = async (e) => {
    e.stopPropagation();
    const ok = await copyShareText(shareBody);
    if (ok) close();
  };

  const onNative = async (e) => {
    e.stopPropagation();
    close();
    await nativeShare({
      title: shareTitle || undefined,
      text: shareBody,
      url
    });
  };

  const showNative = canNativeShare();

  return (
    <div
      className={`share-wrap ${open ? 'share-wrap-open' : ''} ${className}`.trim()}
      ref={wrapRef}
    >
      <button
        type="button"
        className="btn btn-outline btn-sm share-btn"
        onClick={onOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={label}
      >
        🔗 {label}
      </button>

      {open && (
        <div
          className="share-menu"
          role="menu"
          id={menuId}
          aria-label="Opciones para compartir"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="share-menu-title">Compartir</div>

          <button
            type="button"
            role="menuitem"
            className="share-menu-item"
            onClick={onCopyLink}
          >
            <span className="share-menu-ico" aria-hidden="true">🔗</span>
            <span className="share-menu-lbl">Copiar enlace</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="share-menu-item"
            onClick={onCopyText}
          >
            <span className="share-menu-ico" aria-hidden="true">📝</span>
            <span className="share-menu-lbl">Copiar texto para compartir</span>
          </button>

          {showNative && (
            <button
              type="button"
              role="menuitem"
              className="share-menu-item"
              onClick={onNative}
            >
              <span className="share-menu-ico" aria-hidden="true">📤</span>
              <span className="share-menu-lbl">Compartir…</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
